import { formatDate } from '@progress/kendo-intl';
import { EditorUtils, ProseMirror } from '@progress/kendo-react-editor';
import { TextSelection } from 'prosemirror-state';

import { uuid12 } from '../../types/common-helper';
import {
  getBase64AudioExtension,
  getBase64ImageExtension,
  getBase64VideoExtension
} from '../../utils/files';
import { PostResourceAsync } from '../../services/files';
import { Resource } from '../../types/game-document/entities/resource';
import { fileSize } from './insert-media-dialog';

export const onImageInsert = (
  args: any,
  gameId: number,
  version: string,
  onAddToResource: (newResource: Resource, fileName: string) => void,
  onError?: (fileName: string, error: unknown) => void
) => {
  const { files, view, event } = args;
  const nodeType = view.state.schema.nodes.image;

  if (!EditorUtils.canInsert(view.state, nodeType)) return false;

  const position =
    event.type === 'drop'
      ? view.posAtCoords({ left: event.clientX, top: event.clientY })
      : null;

  uploadAndInsertImages({
    view,
    files,
    gameId,
    version,
    nodeType,
    position,
    onAddToResource,
    onError
  });

  return files.length > 0;
};

const getExtensionFromFile = (file: File): string => {
  const nameExt = file.name.split('.').pop();
  if (nameExt && nameExt.length <= 5) return nameExt.toLowerCase();
  return (file.type.split('/')[1] || 'png').toLowerCase();
};

const buildFileName = (file: File, gameId: number, version: string): string => {
  const dateString = formatDate(new Date(), 'yyMMdd');
  const extension = getExtensionFromFile(file);
  return `${gameId}-${dateString}-${version}-${uuid12()}.${extension}`;
};

const uploadAndInsertImages = async ({
  view,
  files,
  gameId,
  version,
  nodeType,
  position,
  attrs = {},
  onAddToResource,
  onError
}: any) => {
  const uploads = await Promise.all(
    (files as File[]).map(async (file) => {
      try {
        if (file.size > fileSize.image) {
          throw new Error('Please reduce your image size below 500KB');
        }

        const fileName = buildFileName(file, gameId, version);
        const uploadFile = new File([file], fileName, { type: file.type });
        const resourceResponse = await PostResourceAsync(uploadFile);
        return {
          file,
          resourceResponse,
          src: resourceResponse.value as string
        };
      } catch (error) {
        onError?.(file.name, error);
        return null;
      }
    })
  );

  const succeeded = uploads.filter(
    (u): u is { file: File; resourceResponse: Resource; src: string } =>
      u !== null
  );

  insertImageFiles({
    view,
    uploads: succeeded,
    nodeType,
    position,
    attrs,
    onAddToResource
  });
};

const insertImageFiles = ({
  view,
  uploads,
  nodeType,
  position,
  attrs,
  onAddToResource
}: any) => {
  let insertPos = position ? position.pos : null;

  uploads.forEach(({ file, resourceResponse, src }: any) => {
    onAddToResource(resourceResponse, file.name);

    const image = nodeType.createAndFill({ ...attrs, src });
    if (!image) return;

    if (insertPos !== null) {
      view.dispatch(view.state.tr.insert(insertPos, image));
      view.focus();
    } else {
      EditorUtils.insertNode(view, image, true);
    }
  });
};

export const insertMediaFiles = ({
  view,
  files,
  nodeType,
  position,
  gameId,
  version,
  toggleSaving,
  onClose,
  onAddToResource,
  attrs = {}
}: any) => {
  if (!EditorUtils.canInsert(view.state, nodeType)) return;

  const file = files[0];
  if (!file) return;

  let reader = new FileReader();
  reader.onload = async (e: ProgressEvent<FileReader>) => {
    if (!e.target) return;

    try {
      let src: string = e.target.result as string;

      const dateString = formatDate(new Date(), 'yyMMdd');
      const fileNamePrefix = `${gameId}-${dateString}-${version}`;
      let extension;

      switch (nodeType.name) {
        case 'audio':
          extension = getBase64AudioExtension(src as string);
          break;
        case 'video':
          extension = getBase64VideoExtension(src as string);
          break;
        case 'image':
          extension = getBase64ImageExtension(src as string);
          break;
      }

      const fileName = `${fileNamePrefix}-${uuid12()}.${extension}`;
      const blob = await fetch(src as string).then((r) => r.blob());
      const extractedBlob: File = new File([blob], fileName, {
        type: blob.type
      });
      const resourceResponse = await PostResourceAsync(extractedBlob);
      src = resourceResponse.value;
      if (onAddToResource)
        onAddToResource(resourceResponse, file.name ?? file.file.name);

      const media = nodeType.createAndFill({
        ...attrs,
        src
      });

      if (position) {
        view.dispatch(view.state.tr.insert(position.pos, media));
      } else {
        EditorUtils.insertNode(view, media, true);
      }
    } catch (error) {
      console.log('Something error when saving media: ', error);
    } finally {
      if (toggleSaving) toggleSaving();
      view.focus();
      if (onClose) onClose();
    }
  };

  const data = 'file' in file ? file.file : file;
  reader.readAsDataURL(data);
};

export const insertVideoPlatform = ({
  view,
  nodeType,
  videoId,
  onClose,
  attrs = {}
}: any) => {
  if (!EditorUtils.canInsert(view.state, nodeType)) return;

  const { state, dispatch } = view;
  const { schema, selection } = state;

  const media = nodeType.create({
    ...attrs,
    videoId
  });

  const paragraph = schema.nodes.paragraph.create();
  let tr = state.tr;

  // replace current selection with media
  tr = tr.replaceRangeWith(selection.from, selection.to, media);
  const mediaEnd = tr.selection.$anchor.pos + media.nodeSize;

  // insert paragraph after media
  tr = tr.insert(mediaEnd, paragraph);
  // move cursor into paragraph
  tr = tr.setSelection(TextSelection.create(tr.doc, mediaEnd + 1));

  dispatch(tr);

  view.focus();
  onClose();
};

export const insertImagePlugin = (onInsert: any) =>
  new ProseMirror.Plugin({
    props: {
      handleDOMEvents: {
        paste: (view: typeof ProseMirror.EditorView, event: ClipboardEvent) =>
          insertImages(view, event, onInsert),
        drop: (view: typeof ProseMirror.EditorView, event: DragEvent) =>
          insertImages(view, event, onInsert)
      }
    },
    key: new ProseMirror.PluginKey('insert-image-plugin')
  });

const insertImages = (view: any, event: any, onInsert: any) => {
  const items =
    (event.clipboardData && event.clipboardData.items) ||
    (event.dataTransfer && event.dataTransfer.files);
  if (items) {
    const files: Array<any> = [];
    let file: any;
    for (let i = 0; i < items.length; i++) {
      file =
        items[i].type.indexOf('image') !== -1
          ? 'getAsFile' in items[i]
            ? items[i].getAsFile()
            : items[i]
          : null;
      if (file) {
        files.push(file);
      }
    }

    if (files.length) {
      event.preventDefault();
    }

    return onInsert({ view, files, event });
  }
};
